import { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";

// Shared marquee-on-hover keyframe — used wherever a name might not fit its
// box (equipped weapon/armor in the HUD toggle, inventory row names). The
// hovered ancestor triggers it via `&:hover .hud-scroll-name.is-scrollable`.
export const hudScrollName = keyframes`
  0%, 15% { transform: translateX(0); }
  85%, 100% { transform: translateX(calc(-1 * var(--scroll-distance, 0px))); }
`;

const ScrollNameWrap = styled.span`
  flex: 1;
  min-width: 0;
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ScrollNameInner = styled.span`
  display: inline-block;
`;

// Truncates with an ellipsis by default; while a `&:hover` ancestor applies
// the `hudScrollName` animation to `.hud-scroll-name.is-scrollable`, slides
// left to reveal the full text if it doesn't fit its box.
export const EquippedName = ({ children }) => {
  const innerRef = useRef(null);
  const [distance, setDistance] = useState(0);

  useEffect(() => {
    const el = innerRef.current;
    const wrap = el?.parentElement;
    if (!el || !wrap) return;
    setDistance(Math.max(0, el.scrollWidth - wrap.clientWidth));
  }, [children]);

  return (
    <ScrollNameWrap>
      <ScrollNameInner
        ref={innerRef}
        className={`hud-scroll-name${distance > 0 ? " is-scrollable" : ""}`}
        style={distance > 0 ? { "--scroll-distance": `${distance}px` } : undefined}
      >
        {children}
      </ScrollNameInner>
    </ScrollNameWrap>
  );
};

export const Panel = styled.div`
  background: ${({ theme }) => theme.colors("background", 0.92)};
  border: 1px solid ${({ theme }) => theme.colors("link", 0.5)};
  padding: 0.4rem 0.75rem;
  color: ${({ theme }) => theme.colors("text")};
  font-family: 'Silkscreen', monospace;
  font-size: 0.65rem;
  min-width: 10rem;
`;

export const Label = styled.span`
  color: ${({ theme }) => theme.colors("foreground", 0.45)};
  margin-right: 0.4rem;
`;

export const SideOverlay = styled.div`
  position: absolute;
  top: 0;
  ${({ $right }) =>
    $right
      ? "right: 0; border-left: 1px solid"
      : "left: 0; border-right: 1px solid"}
  ${({ theme }) => theme.colors("foreground", 0.2)};
  bottom: 0;
  width: 260px;
  background: ${({ theme }) => theme.colors("background", 0.93)};
  z-index: 10010;
  display: flex;
  flex-direction: column;
  font-family: 'Silkscreen', monospace;
  pointer-events: auto;
`;

export const OverlayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors("foreground", 0.12)};
  color: ${({ theme }) => theme.colors("highlight")};
  font-size: 0.7rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
`;

export const CloseBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors("foreground", 0.3)};
  font-family: 'Silkscreen', monospace;
  font-size: 1rem;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  &:hover {
    color: ${({ theme }) => theme.colors("text")};
  }
`;

export const ItemList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
`;

export const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors("foreground", 0.08)};
  &:hover {
    background: ${({ theme }) => theme.colors("chrome")};
  }
  &:hover .hud-scroll-name.is-scrollable {
    animation: ${hudScrollName} 3s ease-in-out infinite;
  }
`;

export const ItemDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

export const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ItemName = styled.div`
  font-size: 0.68rem;
  color: ${({ theme }) => theme.colors("text")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: capitalize;
`;

export const ItemMeta = styled.div`
  font-size: 0.55rem;
  color: ${({ theme }) => theme.colors("foreground", 0.3)};
  text-transform: capitalize;
`;

export const ActionBtn = styled.button`
  background: none;
  border: 1px solid
    ${({ $use, theme }) =>
      $use ? theme.colors("success", 0.4) : theme.colors("danger", 0.3)};
  color: ${({ $use, theme }) =>
    $use ? theme.colors("success") : theme.colors("foreground", 0.3)};
  font-family: 'Silkscreen', monospace;
  font-size: 0.55rem;
  padding: 0.15rem 0.4rem;
  cursor: pointer;
  flex-shrink: 0;
  &:hover {
    border-color: ${({ $use, theme }) =>
      $use ? theme.colors("success") : theme.colors("danger")};
    color: ${({ $use, theme }) =>
      $use ? theme.colors("success") : theme.colors("danger")};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const EmptyMsg = styled.div`
  padding: 1.5rem 1rem;
  color: ${({ theme }) => theme.colors("foreground", 0.2)};
  font-size: 0.65rem;
`;

export const SectionLabel = styled.div`
  padding: 0.35rem 1rem 0.2rem;
  font-size: 0.55rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors("foreground", 0.35)};
  background: ${({ theme }) => theme.colors("background", 0.3)};
  border-bottom: 1px solid ${({ theme }) => theme.colors("foreground", 0.08)};
`;
